// Generate realistic, varied ratings based on image hash
export const generateRatings = (imageData: string) => {
  // Create a simple hash from image data for consistency
  const hash = imageData.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  // Seeded random number generator for consistent results per image
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };

  // Generate base score (6.5 to 9.2 range - more positive and encouraging)
  // Most people will score between 7-8.5 which is realistic and motivating
  const baseScore = 6.5 + seededRandom(hash) * 2.7;
  const overallScore = Math.round(baseScore * 10) / 10;

  // Calculate global rank based on score
  const getGlobalRank = (score: number) => {
    if (score >= 9.0) return "Top 1%";
    if (score >= 8.5) return "Top 5%";
    if (score >= 8.0) return "Top 10%";
    if (score >= 7.5) return "Top 20%";
    if (score >= 7.0) return "Top 30%";
    if (score >= 6.8) return "Top 40%";
    return "Top 50%";
  };

  // Generate regional scores with variance (can go lower for specific regions)
  const generateRegionalScore = (index: number, baseVariance: number = 0) => {
    const variance = seededRandom(hash + index * 100) * 3 - 1.5 + baseVariance;
    const score = Math.max(3.5, Math.min(10.0, baseScore + variance));
    return Math.round(score * 10) / 10;
  };

  // Generate demographic scores (can vary more - some demographics might rate lower)
  const generateDemographicScore = (index: number) => {
    const variance = seededRandom(hash + index * 200) * 3 - 1.5;
    const score = Math.max(4.0, Math.min(10.0, baseScore + variance));
    return Math.round(score * 10) / 10;
  };

  // Generate venue scores with specific variance patterns (can vary significantly)
  const generateVenueScore = (index: number, baseVariance: number = 0) => {
    const variance = seededRandom(hash + index * 300) * 2.8 - 1.4 + baseVariance;
    const score = Math.max(4.5, Math.min(10.0, baseScore + variance));
    return Math.round(score * 10) / 10;
  };

  // Determine ethnicity based on score patterns
  const ethnicities = [
    { country: "Lebanon", region: "Middle East", flag: "🇱🇧", alternatives: ["Syria", "Turkey", "Greece", "Italy"] },
    { country: "Italy", region: "Southern Europe", flag: "🇮🇹", alternatives: ["Greece", "Spain", "Portugal", "Argentina"] },
    { country: "Mexico", region: "Latin America", flag: "🇲🇽", alternatives: ["Colombia", "Spain", "Argentina", "Brazil"] },
    { country: "India", region: "South Asia", flag: "🇮🇳", alternatives: ["Pakistan", "Bangladesh", "Sri Lanka", "Nepal"] },
    { country: "Brazil", region: "Latin America", flag: "🇧🇷", alternatives: ["Portugal", "Argentina", "Colombia", "Spain"] },
    { country: "Turkey", region: "Middle East", flag: "🇹🇷", alternatives: ["Greece", "Lebanon", "Syria", "Italy"] },
    { country: "Philippines", region: "Southeast Asia", flag: "🇵🇭", alternatives: ["Indonesia", "Malaysia", "Mexico", "Spain"] },
    { country: "Egypt", region: "North Africa", flag: "🇪🇬", alternatives: ["Sudan", "Saudi Arabia", "Jordan", "Libya"] },
    { country: "Iran", region: "Middle East", flag: "🇮🇷", alternatives: ["Afghanistan", "Turkey", "Armenia", "Azerbaijan"] },
    { country: "Nigeria", region: "West Africa", flag: "🇳🇬", alternatives: ["Ghana", "Cameroon", "Benin", "Togo"] }
  ];

  const ethnicityIndex = Math.floor(seededRandom(hash + 500) * ethnicities.length);
  const selectedEthnicity = ethnicities[ethnicityIndex];

  // Generate confidence scores
  const primaryConfidence = 75 + Math.floor(seededRandom(hash + 600) * 20);
  const alternativeConfidences = selectedEthnicity.alternatives.map((_, i) => 
    primaryConfidence - 5 - (i * 5) - Math.floor(seededRandom(hash + 700 + i) * 10)
  );

  return {
    overallScore,
    globalRank: getGlobalRank(overallScore),
    ethnicity: {
      primary: {
        country: selectedEthnicity.country,
        region: selectedEthnicity.region,
        flag: selectedEthnicity.flag,
        confidence: primaryConfidence
      },
      alternatives: selectedEthnicity.alternatives.map((country, i) => ({
        country,
        confidence: alternativeConfidences[i],
        flag: getCountryFlag(country)
      }))
    },
    regionalScores: {
      losAngeles: generateRegionalScore(1, 0.5),
      miami: generateRegionalScore(2, 0.3),
      newYork: generateRegionalScore(3, 0),
      chicago: generateRegionalScore(4, 0.2),
      dubai: generateRegionalScore(5, 0.4),
      saoPaulo: generateRegionalScore(6, -0.8),
      buenosAires: generateRegionalScore(7, -0.6),
      tokyo: generateRegionalScore(8, -0.2),
      seoul: generateRegionalScore(9, -0.4),
      london: generateRegionalScore(10, 0.1),
      paris: generateRegionalScore(11, -0.1),
      barcelona: generateRegionalScore(12, 0.5),
      mexicoCity: generateRegionalScore(13, 0.6),
      sydney: generateRegionalScore(14, 0),
      mumbai: generateRegionalScore(15, -0.3),
      lagos: generateRegionalScore(16, -0.2)
    },
    demographicScores: {
      latina: generateDemographicScore(1),
      asian: generateDemographicScore(2),
      middleEastern: generateDemographicScore(3),
      white: generateDemographicScore(4),
      black: generateDemographicScore(5),
      southAsian: generateDemographicScore(6)
    },
    venueScores: {
      gym: generateVenueScore(1, 0.8),
      nightclub: generateVenueScore(2, 0.5),
      bar: generateVenueScore(3, 0.3),
      coffeeshop: generateVenueScore(4, -0.2),
      mall: generateVenueScore(5, -0.1),
      park: generateVenueScore(6, 0.2),
      beach: generateVenueScore(7, 0.6),
      restaurant: generateVenueScore(8, 0.1),
      library: generateVenueScore(9, -0.5),
      concert: generateVenueScore(10, 0.4),
      sportsEvent: generateVenueScore(11, 0.7),
      artGallery: generateVenueScore(12, -0.3),
      bookstore: generateVenueScore(13, -0.4),
      yoga: generateVenueScore(14, 0.3),
      grocery: generateVenueScore(15, -0.1),
      festival: generateVenueScore(16, 0.5)
    },
    facialFeatures: {
      symmetry: generateRegionalScore(20),
      jawline: generateRegionalScore(21),
      eyeArea: generateRegionalScore(22),
      nose: generateRegionalScore(23),
      skinQuality: generateRegionalScore(24),
      smile: generateRegionalScore(25)
    }
  };
};

const getCountryFlag = (country: string): string => {
  const flags: { [key: string]: string } = {
    "Syria": "🇸🇾",
    "Turkey": "🇹🇷",
    "Greece": "🇬🇷",
    "Italy": "🇮🇹",
    "Spain": "🇪🇸",
    "Portugal": "🇵🇹",
    "Argentina": "🇦🇷",
    "Colombia": "🇨🇴",
    "Brazil": "🇧🇷",
    "Pakistan": "🇵🇰",
    "Bangladesh": "🇧🇩",
    "Sri Lanka": "🇱🇰",
    "Nepal": "🇳🇵",
    "Indonesia": "🇮🇩",
    "Malaysia": "🇲🇾",
    "Mexico": "🇲🇽",
    "Sudan": "🇸🇩",
    "Saudi Arabia": "🇸🇦",
    "Jordan": "🇯🇴",
    "Libya": "🇱🇾",
    "Afghanistan": "🇦🇫",
    "Armenia": "🇦🇲",
    "Azerbaijan": "🇦🇿",
    "Ghana": "🇬🇭",
    "Cameroon": "🇨🇲",
    "Benin": "🇧🇯",
    "Togo": "🇹🇬"
  };
  return flags[country] || "🏴";
};